import { TypePullQuote } from '@wren/shared';
import RichText from '../contentful/RichText';

type PullQuoteFields = TypePullQuote<
  'WITHOUT_UNRESOLVABLE_LINKS',
  'en-US'
>['fields'];

export default function PullQuote({
  quote,
  image,
  name,
  title,
  textAlignment,
}: PullQuoteFields) {
  const imageUrl = image?.fields.file?.url;
  const paragraphCount =
    quote?.content?.filter((node) => node.nodeType === 'paragraph').length ?? 0;
  const isMultiParagraph = paragraphCount > 1;

  // Keep the existing defaults when unset: single-paragraph centers, the
  // multi-paragraph block reads left-aligned alongside its bar.
  const alignment = textAlignment ?? (isMultiParagraph ? 'left' : 'center');
  const textAlignClass =
    alignment === 'left'
      ? 'text-left'
      : alignment === 'right'
        ? 'text-right'
        : 'text-center';
  const justifyClass =
    alignment === 'left'
      ? 'justify-start'
      : alignment === 'right'
        ? 'justify-end'
        : 'justify-center';

  const attribution = (imageUrl || name || title) && (
    <div className={`mt-4 flex items-center gap-3 ${justifyClass}`}>
      {imageUrl && (
        <img
          className="h-[46px] w-[46px] shrink-0 rounded-full object-cover"
          src={imageUrl}
          alt={name || ''}
        />
      )}
      {(name || title) && (
        <div className="text-left">
          {name && (
            <div className="font-sans text-sm font-semibold text-gray-1">
              {name}
            </div>
          )}
          {title && (
            <div className="font-sans text-sm italic text-gray-4">{title}</div>
          )}
        </div>
      )}
    </div>
  );

  if (isMultiParagraph) {
    return (
      <div className="relative my-12 mx-auto max-w-xl border-0 border-l-4 border-solid border-wren-orange pl-6">
        <span
          aria-hidden="true"
          className="absolute -top-[10px] left-7 -translate-x-full pr-1 font-serif text-5xl leading-none text-wren-orange opacity-[0.33]"
        >
          &ldquo;
        </span>
        <div
          className={`font-serif text-xl leading-relaxed text-gray-1 [&_p:last-child]:mb-0 ${textAlignClass}`}
        >
          <RichText document={quote} withArticleStyle={false} />
        </div>
        {attribution}
      </div>
    );
  }

  return (
    <div className={`my-12 mx-auto max-w-xl ${textAlignClass}`}>
      <div className="relative font-serif text-2xl leading-snug text-gray-1 [&>div]:inline [&_p]:inline">
        <span className="relative inline-block w-0 align-middle">
          <span
            aria-hidden="true"
            className="absolute right-2 top-1/2 -translate-y-1/2 font-serif text-5xl leading-none text-wren-orange"
          >
            &ldquo;
          </span>
        </span>
        <RichText document={quote} withArticleStyle={false} />
        <span className="relative inline-block w-0 align-middle">
          <span
            aria-hidden="true"
            className="absolute left-2 top-1/2 -translate-y-1/2 font-serif text-5xl leading-none text-wren-orange"
          >
            &rdquo;
          </span>
        </span>
      </div>
      {attribution}
    </div>
  );
}
